import {debounce, type DebouncedFunction} from 'es-toolkit';
import {inject, injectable} from 'inversify';

import {delay, pollAsync} from '@yourcoach/async-utils';

import type {ApiCountResponse} from '../../../../api/types';
import type {CoreUserAccountsCountParams} from '../../../../api/types/core/methods';
import type {Destructible} from '../../../../utils/store';

import {apiRequest} from '../../../../api/utils/apiRequest';
import {CurrentUserService} from '../../../../core/system/currentUser/CurrentUser.service';
import {EnvProviderService} from '../../../../core/system/env/EnvProvider.service';
import {logger} from '../../../../utils/logger';
import {platform} from '../../../../utils/platform';
import {showPhoneNumberRequestAlert} from './showPhoneNumberRequestAlert';

@injectable()
export class RequestPhoneNumberStore implements Destructible {
  private debouncedSaveDontShowAgain: DebouncedFunction<
    (value: boolean) => void
  >;
  private isRequestShown = false;
  private place: RequestPhoneNumberStore.Place = 'enter_app';

  constructor(
    @inject(CurrentUserService)
    private readonly currentUserService: CurrentUserService,
    @inject(EnvProviderService)
    private readonly env: EnvProviderService,
  ) {
    this.debouncedSaveDontShowAgain = debounce(
      this.saveDontShowAgain.bind(this),
      1000,
    );
  }

  init(props: RequestPhoneNumberStore.InitProps) {
    this.onAddPhoneNumberClick = props.onAddPhoneNumberClick;
  }

  destruct() {
    this.debouncedSaveDontShowAgain.cancel();
  }

  async requestPhoneNumberIfNeeded(
    props: RequestPhoneNumberStore.RequestProps,
  ) {
    this.place = props.place;

    if (await this.checkIfRequestShouldBeShown()) {
      this.isRequestShown = true;

      if (props.place === 'enter_app') {
        await delay(3000);
      }

      this.saveShownDate();

      await showPhoneNumberRequestAlert({
        ...props,
        isEmployee: this.currentUserService.user?.isEmployee ?? false,
        onAddPhoneNumberClick: this.onAddPhoneNumberClick,
        onDontShowAgainClick: this.debouncedSaveDontShowAgain,
      });

      this.isRequestShown = false;
    }
  }

  private async checkIfRequestShouldBeShown() {
    if (this.isRequestShown) {
      return false;
    }

    if (!this.currentUserService.user) {
      return false;
    }

    await pollAsync(() =>
      Boolean(this.currentUserService.user?.metadata.onboarding?.is_finished),
    );

    const isDontShowAgain = Boolean(
      this.currentUserService.user.metadata.settings
        ?.dont_show_phone_number_request,
    );

    if (isDontShowAgain) {
      return false;
    }

    const shouldShowByDate = this.checkIfRequestShouldBeShownByDate();

    if (!shouldShowByDate) {
      return false;
    }

    const shouldShowByRole = this.checkIfRequestShouldBeShownByRole();

    if (!shouldShowByRole) {
      return false;
    }

    const hasPhoneNumber = await this.checkIfUserHasPhoneNumber();

    if (hasPhoneNumber) {
      return false;
    }

    return true;
  }

  private checkIfRequestShouldBeShownByDate() {
    if (!this.currentUserService.user) {
      return false;
    }

    const lastShownAt =
      this.currentUserService.user.metadata.settings
        ?.phone_number_request_shown_at;

    if (!lastShownAt) {
      return true;
    }

    const DAYS_TO_SHOW_AGAIN = 3;

    const lastShownAtDate = new Date(lastShownAt);
    const now = new Date();
    const diff = now.getTime() - lastShownAtDate.getTime();
    const diffInDays = diff / (1000 * 60 * 60 * 24);
    const shouldShow = diffInDays > DAYS_TO_SHOW_AGAIN;

    return shouldShow;
  }

  private checkIfRequestShouldBeShownByRole() {
    if (!this.currentUserService.user) {
      return false;
    }

    if (platform.isNative) {
      return this.place === 'enter_app';
    }

    if (this.currentUserService.user.isEmployee) {
      return true;
    }

    return false;
  }

  private async checkIfUserHasPhoneNumber() {
    try {
      const result: ApiCountResponse =
        await apiRequest<CoreUserAccountsCountParams>({
          method: 'user.accounts.count',
          params: {
            query: [['type', '==', 'phone']],
          },
        });

      if (result._count > 0) {
        return true;
      }

      return false;
    } catch (error) {
      return false;
    }
  }

  private async saveDontShowAgain(value: boolean) {
    try {
      const user = this.currentUserService.user;

      if (!user) {
        return;
      }

      user.metadata.update({
        settings: {
          dont_show_phone_number_request: value,
        },
      });

      await this.currentUserService.update(user);
    } catch (error) {
      logger.error(error);
    }
  }

  private async saveShownDate() {
    try {
      const user = this.currentUserService.user;

      if (!user) {
        return;
      }

      user.metadata.update({
        settings: {
          phone_number_request_shown_at: new Date().toISOString(),
        },
      });

      await this.currentUserService.update(user);
    } catch (error) {
      logger.error(error);
    }
  }

  private onAddPhoneNumberClick: RequestPhoneNumberStore.onAddHandler = () => {
    throw new Error('RequestPhoneNumberStore is not initialized properly!');
  };
}

export namespace RequestPhoneNumberStore {
  export type InitProps = {
    onAddPhoneNumberClick: onAddHandler;
  };

  export type onAddHandler = () => void;

  export type Place = 'chat' | 'enter_app' | 'ls_scheduled';

  export type RequestPhoneNumberAlertProps = RequestProps & {
    isEmployee: boolean;
    onAddPhoneNumberClick: onAddHandler;
    onDontShowAgainClick: (value: boolean) => void;
  };

  export type RequestProps = {
    place: Place;
  };
}
